import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Tag, Button, Alert, Spin, Typography, Modal, message } from 'antd';
import Api from '../../helpers/apiHandler';
import { getStripe, formatBillingDate } from '../../helpers/stripe';

const { Text } = Typography;
const api = new Api();

/*
 * BBPIE subscription (Stripe Billing): GET billing/subscription/{companyId}.
 * Everyone sees the status; only admins (canManage) get Cancel / Resume / Reactivate / Confirm payment
 * (the backend enforces this too).
 * Cancelling ends the subscription at the end of the current period (or trial); access continues until then.
 * Dates come as UTC instants and are shown in the company's billing timezone (timezoneId).
 */

const STATUS_TAGS = {
  trial: { color: 'blue', label: 'Trial' },
  active: { color: 'green', label: 'Active' },
  past_due: { color: 'orange', label: 'Past due' },
  inactive: { color: 'red', label: 'Ended' },
};

const formatAmount = (amount) => (amount != null ? `$${Number(amount).toFixed(2)}` : '-');

const Field = ({ label, children }) => (
  <Col xs={24} sm={12} md={6} style={{ marginBottom: 12 }}>
    <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{label}</Text>
    <Text strong>{children}</Text>
  </Col>
);

const SubscriptionStatus = ({ companyId, canManage = false, refreshKey = 0, onStatusChange }) => {
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const updateSubscription = useCallback((data) => {
    setSubscription(data);
    if (onStatusChange) onStatusChange(data);
  }, [onStatusChange]);

  const fetchStatus = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await api.get(`billing/subscription/${companyId}`);
      if (res && res.status === 1 && res.data) {
        updateSubscription(res.data);
        setNotFound(false);
      } else {
        updateSubscription(null);
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  }, [companyId, updateSubscription]);

  // refreshKey changes when cards change (the first card starts the trial).
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus, refreshKey]);

  const tz = subscription && subscription.timezoneId;
  const date = (value) => formatBillingDate(value, tz);

  // Runs 3-D Secure for an invoice that needs the customer's confirmation.
  const confirmPayment = async (data) => {
    const stripePromise = getStripe(data.publishableKey);
    if (!stripePromise || !data.clientSecret) {
      message.error('Card payments are not configured. Please contact support.');
      return;
    }
    const stripe = await stripePromise;
    const result = await stripe.confirmCardPayment(data.clientSecret);
    if (result.error) {
      message.error(result.error.message || 'Payment could not be confirmed.');
    } else {
      message.success('Payment confirmed. Your subscription will update shortly.');
      // Stripe webhooks update the status; give them a moment before refreshing.
      setTimeout(fetchStatus, 4000);
    }
  };

  const runAction = async (path, successText) => {
    if (working) return; // no double submits
    setWorking(true);
    try {
      const res = await api.post(path, { data: { companyId } });
      if (res && res.status === 1) {
        message.success(res.message || successText);
        if (res.data) {
          updateSubscription(res.data);
          if (res.data.requiresAction) {
            await confirmPayment(res.data);
          }
        }
      } else {
        message.error((res && res.message) || 'Something went wrong. Please try again.');
      }
    } catch (e) {
      message.error('Something went wrong. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const handleCancel = () => {
    const endDate = subscription.status === 'trial'
      ? subscription.trialEnd || subscription.currentPeriodEnd
      : subscription.currentPeriodEnd;
    Modal.confirm({
      title: 'Cancel subscription',
      content: `Your subscription will end on ${date(endDate)}. You keep full access until then and will not be charged again.`,
      okText: 'Cancel subscription',
      okType: 'danger',
      cancelText: 'Keep subscription',
      onOk: () => runAction('billing/subscription/cancel', 'Subscription cancelled'),
    });
  };

  const handleResume = () => {
    Modal.confirm({
      title: 'Resume subscription',
      content: 'Your subscription will continue and renew automatically.',
      okText: 'Resume',
      onOk: () => runAction('billing/subscription/resume', 'Subscription resumed'),
    });
  };

  const handleReactivate = () => {
    const users = subscription.billableUsers;
    Modal.confirm({
      title: 'Reactivate subscription',
      content: `Your default card will be charged ${formatAmount(subscription.amountPerPeriod)} now`
        + (users ? ` for ${users} user${users === 1 ? '' : 's'}` : '')
        + ', and then every month. There is no new free trial.',
      okText: 'Reactivate',
      onOk: () => runAction('billing/subscription/reactivate', 'Subscription reactivated'),
    });
  };

  const handleConfirmPayment = async () => {
    if (working) return;
    setWorking(true);
    try {
      await confirmPayment(subscription);
    } catch (e) {
      message.error('Payment could not be confirmed.');
    } finally {
      setWorking(false);
    }
  };

  if (loading && !subscription) {
    return (
      <Card title="Subscription" style={{ marginBottom: 24 }}>
        <div style={{ textAlign: 'center' }}><Spin /></div>
      </Card>
    );
  }

  if (notFound || !subscription || (!subscription.status && !subscription.stripeSubscriptionId)) {
    return (
      <Card title="Subscription" style={{ marginBottom: 24 }}>
        <Text type="secondary">
          No active subscription. Add a payment method to start your 30-day free trial.
        </Text>
      </Card>
    );
  }

  const tag = STATUS_TAGS[subscription.status] || { color: 'default', label: subscription.status || 'Unknown' };
  const isLive = subscription.status && subscription.status !== 'inactive';
  const endDate = subscription.status === 'trial'
    ? subscription.trialEnd || subscription.currentPeriodEnd
    : subscription.currentPeriodEnd;

  let action = null;
  if (canManage && isLive) {
    action = subscription.cancelAtPeriodEnd
      ? <Button type="primary" loading={working} disabled={working} onClick={handleResume}>Resume subscription</Button>
      : <Button type="danger" ghost loading={working} disabled={working} onClick={handleCancel}>Cancel subscription</Button>;
  } else if (canManage && subscription.canReactivate) {
    action = <Button type="primary" loading={working} disabled={working} onClick={handleReactivate}>Reactivate subscription</Button>;
  }

  return (
    <Card
      title={<span>Subscription <Tag color={tag.color} style={{ marginLeft: 8 }}>{tag.label}</Tag></span>}
      style={{ marginBottom: 24 }}
      extra={action}
    >
      {!isLive && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={canManage
            ? 'Your subscription has ended. Reactivate it to continue using the app.'
            : 'Your company subscription has ended. Please contact your administrator.'}
        />
      )}
      {subscription.requiresAction && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Your bank needs you to confirm the latest payment."
          description={canManage
            ? <Button size="small" type="primary" loading={working} disabled={working} onClick={handleConfirmPayment}>Confirm payment</Button>
            : 'Please ask your administrator to confirm the payment.'}
        />
      )}
      {subscription.status === 'past_due' && !subscription.requiresAction && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="The last payment failed. Stripe will retry automatically; please make sure your default card is valid."
        />
      )}
      {subscription.cancelAtPeriodEnd && isLive && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Your subscription is cancelled and ends on ${date(endDate)}. You keep access until then.`}
        />
      )}

      <Row gutter={16}>
        {subscription.status === 'trial' && (
          <Field label="Trial ends">{date(subscription.trialEnd || subscription.currentPeriodEnd)}</Field>
        )}
        {isLive && subscription.status !== 'trial' && (
          <Field label={subscription.cancelAtPeriodEnd ? 'Ends on' : 'Next billing date'}>
            {date(subscription.currentPeriodEnd)}
          </Field>
        )}
        {!isLive && (
          <Field label="Ended on">{date(subscription.currentPeriodEnd)}</Field>
        )}
        <Field label="Users">
          {!isLive && subscription.billableUsers != null ? subscription.billableUsers
            : (subscription.quantity != null ? subscription.quantity : '-')}
        </Field>
        <Field label={isLive ? 'Estimated monthly amount' : 'Monthly amount if reactivated'}>
          {formatAmount(subscription.amountPerPeriod)}
        </Field>
        <Field label="Last payment">
          {subscription.lastBillingAt
            ? `${subscription.lastBillingStatus || ''} ${date(subscription.lastBillingAt)}`
            : '-'}
        </Field>
      </Row>
      {tz && (
        <Text type="secondary" style={{ fontSize: 12 }}>Dates are shown in {tz} time.</Text>
      )}
    </Card>
  );
};

export default SubscriptionStatus;
